import React, { useState, useEffect } from "react";
import { Layout, Table, Button, Space, Descriptions, Card, Tag, message, Row, Col, Input, InputNumber, Typography } from "antd";
import { useParams, useNavigate } from "react-router-dom";
import Headerdiv from "../../layout/header";
import Footerdiv from "../../layout/footer";
import { FileTextOutlined, CheckCircleOutlined, CloseCircleOutlined } from "@ant-design/icons";
import api from "../../../services/axiosInstance";
import dayjs from "dayjs";
import "../../../styles/fluidDesign.css";
import AppBreadcrumb from "../../../components/common/AppBreadcrumb";
import { getStatusColor } from "../../../utils/statusHelper";

const { Content } = Layout;
const { TextArea } = Input;
const { Text } = Typography;

const RequestApprovalDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [mrMaster, setMrMaster] = useState(null);
  const [reviewItems, setReviewItems] = useState([]);
  const [reviewComment, setReviewComment] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchMRDetails();
  }, [id]);

  const fetchMRDetails = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/inventory/material_request/get.php?mr_id=${id}`);
      if (res.data && res.data.status === "success" && res.data.dataset) {
        const data = res.data.dataset;
        setMrMaster(data);
        const isPendingStatus = data.req_status === "Pending";
        setReviewItems((data.items || []).map(item => ({
          ...item,
          accept_qty: isPendingStatus ? Number(item.request_qty) : (item.accept_qty !== null && item.accept_qty !== undefined ? Number(item.accept_qty) : Number(item.request_qty)),
          accept_comment: item.accept_comment || ""
        })));
      } else {
        message.error("Material Request not found.");
        navigate("/inventory/material-request/approvals");
      }
    } catch (error) {
      message.error("Failed to load Material Request details.");
      navigate("/inventory/material-request/approvals");
    } finally {
      setLoading(false);
    }
  };

  const handleQtyChange = (mri_id, val) => {
    setReviewItems(prev => prev.map(item => {
      if (item.mri_id === mri_id) {
        return { ...item, accept_qty: val };
      }
      return item;
    }));
  };

  const handleItemCommentChange = (mri_id, val) => {
    setReviewItems(prev => prev.map(item => {
      if (item.mri_id === mri_id) {
        return { ...item, accept_comment: val };
      }
      return item;
    }));
  };

  const handleReviewAction = async (actionType) => {
    if (!mrMaster) return;
    
    if (actionType === 'Reject' && !reviewComment.trim()) {
      message.warning("Please provide a reason / comment for rejection.");
      return;
    }

    setSubmitting(true);
    try {
      // Prepare items payload
      const payloadItems = reviewItems.map(item => ({
        mri_id: Number(item.mri_id),
        accept_qty: Number(item.accept_qty),
        accept_comment: item.accept_comment || ""
      }));

      const response = await api.post("/inventory/material_request/approve.php", {
        mr_id: Number(id),
        action: actionType,
        comment: reviewComment,
        items: actionType === 'Approve' ? payloadItems : []
      });

      if (response.data.status === "success") {
        message.success(response.data.message || `Material Request successfully ${actionType.toLowerCase()}ed.`);
        navigate("/inventory/material-request/approvals");
      } else {
        message.error(response.data.message || `Failed to ${actionType.toLowerCase()} material request.`);
      }
    } catch (error) {
      message.error("An error occurred during review.");
    } finally {
      setSubmitting(false);
    }
  };

  const renderStatusTag = (status) => {
    return <Tag color={getStatusColor(status)} style={{ fontWeight: "600" }}>{status}</Tag>;
  };

  const isPending = mrMaster && mrMaster.req_status === "Pending";

  const columns = [
    { title: "Item Code", dataIndex: "in_code", key: "in_code" },
    { title: "Item Name", dataIndex: "in_name", key: "in_name" },
    { title: "UOM", dataIndex: "uom_name", key: "uom_name", render: (val) => val || "-" },
    { title: "Requested Qty", dataIndex: "request_qty", key: "request_qty", align: "right", render: (qty) => <strong>{parseFloat(qty || 0).toFixed(2)}</strong> },
    {
      title: "Available Qty",
      dataIndex: "current_stock",
      key: "current_stock",
      align: "right",
      render: (val, record) => {
        const stock = parseFloat(val || 0);
        const requested = parseFloat(record?.request_qty || 0);
        return val === null || val === undefined ? (
          <span>-</span>
        ) : (
          <strong style={{ color: stock <= 0 ? '#ff4d4f' : (stock < requested ? '#faad14' : 'inherit') }}>
            {stock.toFixed(2)}
          </strong>
        );
      }
    },
    {
      title: isPending ? "Approved Qty" : "Accepted Qty",
      key: "accept_qty",
      align: "right",
      render: (_, record) => {
        if (isPending) {
          return (
            <InputNumber
              min={0}
              max={Number(record.request_qty)}
              value={record.accept_qty}
              onChange={(val) => handleQtyChange(record.mri_id, val)}
              size="middle"
              style={{ width: '120px' }}
            />
          );
        }
        return parseFloat(record.accept_qty || 0).toFixed(2);
      }
    },
    {
      title: "Item Note / Comment",
      key: "accept_comment",
      render: (_, record) => {
        if (isPending) {
          return (
            <Input
              placeholder="Add item comment..."
              value={record.accept_comment}
              onChange={(e) => handleItemCommentChange(record.mri_id, e.target.value)}
              className="fluid-input"
            />
          );
        }
        return record.accept_comment || "-";
      }
    }
  ];

  return (
    <Layout className="fluid-page">
      <Headerdiv />

      <div className="fluid-page-header">
        <div className="floating-shape shape-1"></div>
        <div className="floating-shape shape-2"></div>
        <AppBreadcrumb items={[
          { label: 'Inventory', link: '/inventory-items' },
          { label: 'Material Request Approvals', link: '/inventory/material-request/approvals' },
          { label: 'Review Request' },
        ]} />
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', flexWrap: 'wrap', gap: '16px' }}>
          <div>
            <h1 className="fluid-page-title" style={{ margin: 0 }}>
              <FileTextOutlined style={{ marginRight: 12 }} />
              Review Request: {mrMaster ? mrMaster.mr_ref : "Loading..."}
            </h1>
            
          </div>
        </div>
      </div>

      <Content className="fluid-content" style={{ minHeight: "calc(100vh - 350px)" }}>
        {loading || !mrMaster ? (
          <Card className="fluid-card" style={{ textAlign: "center", padding: "40px 0" }}>
            <Text style={{ color: "var(--fluid-primary)" }}>Loading material request details...</Text>
          </Card>
        ) : (
          <Space direction="vertical" size="large" style={{ display: 'flex', width: '100%' }}>
            <Card className="fluid-card" title="Request Details Overview">
              <Descriptions bordered column={{ xxl: 4, xl: 3, lg: 3, md: 2, sm: 1, xs: 1 }} size="small">
                <Descriptions.Item label="Request Reference"><strong>{mrMaster.mr_ref}</strong></Descriptions.Item>
                <Descriptions.Item label="Required Date">{dayjs(mrMaster.required_date).format("YYYY-MM-DD")}</Descriptions.Item>
                <Descriptions.Item label="Status">{renderStatusTag(mrMaster.req_status)}</Descriptions.Item>
                <Descriptions.Item label="Created By">{mrMaster.creator_name ? mrMaster.creator_name.charAt(0).toUpperCase() + mrMaster.creator_name.slice(1) : "-"}</Descriptions.Item>
                <Descriptions.Item label="Requested Date">{dayjs(mrMaster.req_dt).format("YYYY-MM-DD hh:mm:ss A")}</Descriptions.Item>
                {mrMaster.reviewer_name && (
                  <>
                    <Descriptions.Item label="Reviewed By">{mrMaster.reviewer_name ? mrMaster.reviewer_name.charAt(0).toUpperCase() + mrMaster.reviewer_name.slice(1) : "-"}</Descriptions.Item>
                    <Descriptions.Item label="Review Date">
                      {mrMaster.req_approved_dt 
                        ? dayjs.unix(Number(mrMaster.req_approved_dt)).format("YYYY-MM-DD hh:mm:ss A") 
                        : "-"}
                    </Descriptions.Item>
                  </>
                )}
              </Descriptions>
            </Card>

            <Card className="fluid-card" title="Requested Items Review">
              <Table
                dataSource={reviewItems}
                rowKey="mri_id"
                pagination={false}
                bordered
                size="middle"
                scroll={{ x: "max-content" }}
                columns={columns}
              />
            </Card>

            {isPending && (
              <Card className="fluid-card" title="Review Comments / Notes">
                <div style={{ marginBottom: 24 }}>
                  <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
                    Provide notes for the action. Note that a comment is required if rejecting the request.
                  </Text>
                  <TextArea
                    rows={4}
                    value={reviewComment}
                    onChange={(e) => setReviewComment(e.target.value)}
                    placeholder="Enter review comments or rejection reason..."
                    className="fluid-input"
                  />
                </div>

                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '16px' }}>
                  <Button 
                    icon={<CloseCircleOutlined />} 
                    onClick={() => handleReviewAction('Reject')}
                    loading={submitting}
                    className="fluid-btn-secondary"
                    style={{ minWidth: '150px', color: '#ff4d4f', borderColor: '#ff4d4f' }}
                  >
                    Reject
                  </Button>
                  <Button 
                    type="primary" 
                    icon={<CheckCircleOutlined />} 
                    onClick={() => handleReviewAction('Approve')}
                    loading={submitting}
                    className="fluid-btn-primary"
                    style={{ minWidth: '150px', background: '#52c41a', borderColor: '#52c41a' }}
                  >
                    Approve
                  </Button>
                </div>
              </Card>
            )}

            {!isPending && mrMaster.reviewer_comment && (
              <Card className="fluid-card" title="Reviewer Feedback">
                <Descriptions column={1}>
                  <Descriptions.Item label="Reviewer Notes">
                    <div style={{ background: '#f5f5f5', padding: '12px 16px', borderRadius: '6px', width: '100%', marginTop: '4px' }}>
                      {mrMaster.reviewer_comment}
                    </div>
                  </Descriptions.Item>
                </Descriptions>
              </Card>
            )}
          </Space>
        )}
      </Content>
      <Footerdiv />
    </Layout>
  );
};

export default RequestApprovalDetail;
